import { CampaignSummary, CampaignType } from '../../lib/types';
import { parseDate, dayDiff, today } from '../../lib/dates';

interface GanttProps {
  campaigns: CampaignSummary[];
  onCampaignClick: (id: string) => void;
  rangeWeeks?: number;
  startOffsetWeeks?: number;
  compact?: boolean;
}

const typeColor = (t: CampaignType): string =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

const typeBg = (t: CampaignType): string =>
  t === 'Promotion' ? 'var(--sitewide-bg)' : t === 'Brand' ? 'var(--brand-bg)' : 'var(--seasonal-bg)';

export const GanttTimeline = ({
  campaigns,
  onCampaignClick,
  rangeWeeks = 12,
  startOffsetWeeks = -2,
  compact = false,
}: GanttProps) => {
  const TODAY = today();

  // Monday-aligned start date
  const start = new Date(TODAY);
  start.setDate(start.getDate() + startOffsetWeeks * 7);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  const totalDays = rangeWeeks * 7;

  // Months (variable-width headers)
  const months: { key: string; label: string; count: number }[] = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const last = months[months.length - 1];
    if (!last || last.key !== key) {
      months.push({ key, label: d.toLocaleDateString('en', { month: 'short' }), count: 1 });
    } else {
      last.count++;
    }
  }

  // Week headers (one per 7 days)
  const weeks: { index: number; date: Date }[] = [];
  for (let i = 0; i < totalDays; i += 7) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    weeks.push({ index: i, date: d });
  }

  const colW = compact ? 10 : 14;
  const rowH = compact ? 28 : 34;
  const childRowH = compact ? 24 : 28;
  const labelW = compact ? 200 : 240;
  const totalW = totalDays * colW;

  // Build rows: each parent followed by its present children.
  // Undated campaigns ("To plan") don't appear on the Gantt.
  const pool = [...campaigns]
    .filter((c): c is typeof c & { start_date: string; end_date: string } =>
      !!c.start_date && !!c.end_date)
    .sort((a, b) => parseDate(a.start_date).getTime() - parseDate(b.start_date).getTime());
  const presentIds = new Set(pool.map((c) => c.id));
  const parents = pool.filter((c) => !c.parent || !presentIds.has(c.parent));
  interface Row {
    c: CampaignSummary;
    depth: number;
    h: number;
    top: number;
  }
  const ordered: Omit<Row, 'top'>[] = [];
  parents.forEach((p) => {
    ordered.push({ c: p, depth: 0, h: rowH });
    pool
      .filter((k) => k.parent === p.id)
      .forEach((ch) => ordered.push({ c: ch, depth: 1, h: childRowH }));
  });
  pool.forEach((c) => {
    if (!ordered.find((r) => r.c.id === c.id)) ordered.push({ c, depth: 0, h: rowH });
  });

  let cum = 0;
  const rows: Row[] = ordered.map((r) => {
    const top = cum;
    cum += r.h;
    return { ...r, top };
  });
  const totalH = cum;
  const todayOffset = dayDiff(start, TODAY) * colW;

  return (
    <div className="gantt">
      <div className="gantt-scroll">
        <div className="gantt-inner" style={{ width: labelW + totalW }}>
          <div className="gantt-header" style={{ gridTemplateColumns: `${labelW}px ${totalW}px` }}>
            <div className="gantt-corner">Campaign</div>
            <div style={{ position: 'relative' }}>
              <div className="gantt-months">
                {months.map((m) => (
                  <div
                    key={m.key}
                    className="gantt-month"
                    style={{ width: m.count * colW, borderRight: '1px solid var(--border-soft)' }}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              <div className="gantt-weeks">
                {weeks.map((w) => (
                  <div key={w.index} className="gantt-week" style={{ width: 7 * colW }}>
                    <span>{w.date.getDate()}</span>
                  </div>
                ))}
              </div>
              {todayOffset >= 0 && todayOffset <= totalW && (
                <div className="gantt-today-pill" style={{ left: todayOffset }}>Today</div>
              )}
            </div>
          </div>

          <div className="gantt-body" style={{ gridTemplateColumns: `${labelW}px ${totalW}px` }}>
            <div className="gantt-labels">
              {rows.map((r) => (
                <div
                  key={r.c.id}
                  className="gantt-label"
                  style={{ height: r.h, paddingLeft: 14 + r.depth * 18 }}
                  onClick={() => onCampaignClick(r.c.id)}
                >
                  {r.depth > 0 && (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        marginRight: 2,
                        borderLeft: '1px solid var(--border-strong)',
                        borderBottom: '1px solid var(--border-strong)',
                        borderBottomLeftRadius: 3,
                        display: 'inline-block',
                        alignSelf: 'flex-start',
                        marginTop: 10,
                      }}
                    />
                  )}
                  <span
                    className="chip"
                    style={{
                      background: typeBg(r.c.type),
                      color: typeColor(r.c.type),
                      padding: '1px 6px',
                      fontSize: 10.5,
                    }}
                  >
                    <span className="dot" style={{ background: typeColor(r.c.type) }} />
                    {r.c.type[0]}
                  </span>
                  <span
                    className="gantt-label-name"
                    style={{
                      fontSize: r.depth > 0 ? 11.5 : 12.5,
                      color: r.depth > 0 ? 'var(--ink-2)' : 'var(--ink)',
                    }}
                  >
                    {r.c.name}
                  </span>
                </div>
              ))}
            </div>
            <div className="gantt-grid" style={{ height: totalH, width: totalW }}>
              {weeks.map((w) => (
                <div
                  key={w.index}
                  className="gantt-gridcol"
                  style={{ left: w.index * colW, width: 7 * colW }}
                />
              ))}
              {todayOffset >= 0 && todayOffset <= totalW && (
                <div className="gantt-today" style={{ left: todayOffset }} />
              )}
              {rows.map((r) => {
                const c = r.c;
                // `pool` was already filtered to dated campaigns above,
                // so these are non-null at runtime.
                const s = parseDate(c.start_date as string);
                const e = parseDate(c.end_date as string);
                const rawStartX = dayDiff(start, s) * colW;
                const rawEndX = (dayDiff(start, e) + 1) * colW;
                // Clip the bar to the visible window — campaigns that started
                // before the window or end after it shouldn't bleed into the
                // labels column or past the grid.
                const x = Math.max(0, rawStartX);
                const endX = Math.min(totalW, rawEndX);
                const w = Math.max(0, endX - x);
                // Skip campaigns entirely outside the window
                if (w === 0) return null;
                const isPast = e < TODAY;
                const barH = r.depth > 0 ? 16 : 20;
                return (
                  <div
                    key={c.id}
                    className="gantt-bar"
                    onClick={() => onCampaignClick(c.id)}
                    style={{
                      top: r.top + (r.h - barH) / 2,
                      left: x,
                      width: Math.max(24, w),
                      height: barH,
                      background: isPast
                        ? 'transparent'
                        : r.depth > 0
                        ? 'var(--bg-elevated)'
                        : typeBg(c.type),
                      borderColor: typeColor(c.type),
                      color: typeColor(c.type),
                      opacity: isPast ? 0.55 : 1,
                      fontSize: r.depth > 0 ? 10.5 : 11.5,
                    }}
                  >
                    <span className="gantt-bar-dot" style={{ background: typeColor(c.type) }} />
                    <span className="gantt-bar-name">
                      {r.depth > 0 ? '↳ ' : ''}
                      {c.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttTimeline;
